import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import './FournisseurDocumentFormPage.css';

const API_URL = import.meta.env.VITE_API_URL;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const docTypeLabel = (type) => (
  type === 'bon_livraison' ? 'Bon de Livraison' : 'Bon de Commande'
);

export const isProduitLineaire = (produit) => produit?.modeGestionStock === 'lineaire';

export const formatStockQuantity = (quantite, isLineaire = false) => {
  const num = Number(quantite || 0);
  const formatted = num.toLocaleString('fr-FR').replace(/\s/g, '\u00A0');
  return `${formatted}${isLineaire ? '\u00A0m' : ''}`;
};

export const getPrixUnitaireAchat = (produit) => {
  if (!produit) return 0;
  if (isProduitLineaire(produit)) {
    const prixMetre = Number(produit.prixMetre) || 0;
    if (prixMetre > 0) return prixMetre;
  }
  return Number(produit.prixAchat) || 0;
};

export const getProduitTva = (produit) => {
  const tva = Number(produit?.TVA ?? produit?.tva);
  return Number.isFinite(tva) && tva >= 0 ? tva : 19;
};

export const getProduitRemise = (produit) => {
  const remise = Number(produit?.remise ?? produit?.remiseAchat);
  if (!Number.isFinite(remise)) return 0;
  return Math.min(100, Math.max(0, remise));
};

export const buildLinePricing = (produit, overrides = {}) => {
  const source = overrides || {};
  const prixUnitaire = Number(source.prixUnitaire ?? source.prixAchat ?? source.prixMetre);
  const tva = Number(source.tva ?? source.TVA);
  const remise = Number(source.remise);
  const remise2 = Number(source.remise2);

  return {
    prixUnitaire: Number.isFinite(prixUnitaire) && prixUnitaire >= 0 ? prixUnitaire : getPrixUnitaireAchat(produit),
    tva: Number.isFinite(tva) && tva >= 0 ? tva : getProduitTva(produit),
    remise: Number.isFinite(remise) ? Math.min(100, Math.max(0, remise)) : getProduitRemise(produit),
    remise2: Number.isFinite(remise2) ? Math.min(100, Math.max(0, remise2)) : 0
  };
};

export const calculateLineAmounts = (quantite, line) => {
  const qte = Math.max(0, Number(quantite) || 0);
  const prixUnitaire = Math.max(0, Number(line?.prixUnitaire) || 0);
  const tva = Math.max(0, Number(line?.tva ?? 19));
  const remise = Math.min(100, Math.max(0, Number(line?.remise) || 0));
  const remise2 = Math.min(100, Math.max(0, Number(line?.remise2) || 0));

  const brut = qte * prixUnitaire;
  const montantRemise1 = brut * (remise / 100);
  const baseR1 = Math.max(0, brut - montantRemise1);
  const montantRemise2 = baseR1 * (remise2 / 100);
  const montantRemise = montantRemise1 + montantRemise2;
  const montantHT = Math.max(0, baseR1 - montantRemise2);
  const montantTVA = montantHT * (tva / 100);

  return {
    brut,
    montantRemise1,
    montantRemise2,
    montantRemise,
    montantHT,
    montantTVA,
    montantTTC: montantHT + montantTVA
  };
};

export const normalizeDecoupe = (decoupe) => {
  const nombrePieces = Math.max(1, parseInt(decoupe?.nombrePieces, 10) || 1);
  const longueurPieceMetre = Math.max(0.001, Number(decoupe?.longueurPieceMetre) || 0.001);

  return {
    nombrePieces,
    longueurPieceMetre,
    metres: Number((nombrePieces * longueurPieceMetre).toFixed(3))
  };
};

export const recalculateLineaireEntry = (entry) => {
  const baseDecoupes = Array.isArray(entry?.decoupes) && entry.decoupes.length > 0
    ? entry.decoupes
    : [{ nombrePieces: entry?.nombrePieces, longueurPieceMetre: entry?.longueurPieceMetre }];
  const decoupes = baseDecoupes.map(normalizeDecoupe);
  const metres = Number(decoupes.reduce((sum, d) => sum + d.metres, 0).toFixed(3));

  return { mode: 'lineaire', decoupes, metres };
};

export const buildDefaultSelection = (produit) => {
  const pricing = buildLinePricing(produit);

  if (isProduitLineaire(produit)) {
    return {
      mode: 'lineaire',
      decoupes: [{ nombrePieces: 1, longueurPieceMetre: 1 }],
      metres: 1,
      ...pricing
    };
  }

  return { mode: 'unite', qte: 1, ...pricing };
};

export const buildConversionProduitsQte = (doc, produits = []) => {
  if (!doc || !Array.isArray(doc.articles)) return {};
  const next = {};

  doc.articles.forEach((article) => {
    const produitId = article.produitId?._id || article.produitId;
    if (!produitId) return;
    const produit = produits.find(p => String(p._id) === String(produitId));
    const isLineaire = article.lineaire || isProduitLineaire(produit);
    const pricing = buildLinePricing(produit, article);

    if (isLineaire) {
      const rawDecoupes = Array.isArray(article.decoupes) && article.decoupes.length > 0
        ? article.decoupes
        : [{ nombrePieces: 1, longueurPieceMetre: Number(article.quantite) || 1 }];
      next[produitId] = { ...recalculateLineaireEntry({ decoupes: rawDecoupes }), ...pricing };
    } else {
      next[produitId] = {
        mode: 'unite',
        qte: Math.max(1, parseInt(article.quantite, 10) || 1),
        ...pricing
      };
    }
  });

  return next;
};

const formatCurrency = (value) =>
  `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} TND`;

const getTypeId = (produit) => produit?.typeProduit?._id || produit?.typeProduit || '';
const getFournisseurTypes = (fournisseur) => fournisseur?.typeProduit || [];
const sameId = (a, b) => String(a || '') === String(b || '');

const FournisseurDocumentFormPage = ({
  fournisseurs = [],
  produits = [],
  types = [],
  fixedFournisseur = null,
  fixedFournisseurId = '',
  initialType = 'bon_commande',
  initialDoc = null,
  initialProduitsQte = null,
  initialFournisseurId = '',
  initialProduitId = '',
  sourceBonCommandeId = null,
  mode = 'create',
  onBack,
  onCreated,
  refreshHistorique,
  contextLabel = 'Achats fournisseur'
}) => {
  const isEditMode = mode === 'edit' && Boolean(initialDoc?._id);
  const sourceId = sourceBonCommandeId || (
    isEditMode
      ? (initialDoc?.bonCommandeRef?._id || initialDoc?.bonCommandeRef || null)
      : initialDoc?._id || null
  );
  const sourceSupplierId = initialDoc?.fournisseur?._id || initialDoc?.fournisseur || '';
  const selectedFixedSupplierId = fixedFournisseur?._id || fixedFournisseurId || '';
  const isFixedSupplier = Boolean(selectedFixedSupplierId);
  const prefilledProduitsQte = initialProduitsQte || (
    initialProduitId
      ? { [initialProduitId]: buildDefaultSelection(produits.find(p => sameId(p._id, initialProduitId))) }
      : buildConversionProduitsQte(initialDoc, produits)
  );
  const firstConvertedProductId = Object.keys(prefilledProduitsQte)[0];
  const firstConvertedProduct = produits.find(p => sameId(p._id, firstConvertedProductId));

  const [docType, setDocType] = useState(initialType === 'bon_livraison' ? 'bon_livraison' : 'bon_commande');
  const [selectedTypeId, setSelectedTypeId] = useState(getTypeId(firstConvertedProduct));
  const [selectedFournisseurId, setSelectedFournisseurId] = useState(
    selectedFixedSupplierId || sourceSupplierId || initialFournisseurId || ''
  );
  const [produitsQte, setProduitsQte] = useState(prefilledProduitsQte);
  const [remise2Mode, setRemise2Mode] = useState(initialDoc?.remise2Mode || 'aucun');
  const [remise2Val, setRemise2Val] = useState(initialDoc?.remise2Val !== undefined ? initialDoc.remise2Val : 0);
  const [remise2Type, setRemise2Type] = useState(initialDoc?.remise2Type || 'pourcentage');
  const [docDescription, setDocDescription] = useState(initialDoc?.description || '');
  const [docNotes, setDocNotes] = useState(initialDoc?.notes || '');
  const [docConditions, setDocConditions] = useState(initialDoc?.conditions || '');
  const [docFrais, setDocFrais] = useState(Array.isArray(initialDoc?.fraisSupplementaires) ? initialDoc.fraisSupplementaires : []);
  
  const [fodecApplicable, setFodecApplicable] = useState(initialDoc?.fodecApplicable ?? false);
  const [tauxFodec, setTauxFodec] = useState(initialDoc?.tauxFodec ?? 1);
  const [timbreFiscalApplicable, setTimbreFiscalApplicable] = useState(initialDoc?.timbreFiscalApplicable ?? true);

  const [sendBonCommandeEmail, setSendBonCommandeEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isFixedSupplier) setSelectedFournisseurId(selectedFixedSupplierId);
  }, [isFixedSupplier, selectedFixedSupplierId]);

  useEffect(() => {
    if (!initialProduitId || produits.length === 0) return;
    const produit = produits.find(p => sameId(p._id, initialProduitId));
    if (!produit) return;
    if (!selectedTypeId) setSelectedTypeId(getTypeId(produit));
    setProduitsQte(prev => {
      if (prev[initialProduitId] !== undefined) return prev;
      return { ...prev, [initialProduitId]: buildDefaultSelection(produit) };
    });
  }, [initialProduitId, produits, selectedTypeId]);

  const selectedFournisseur = useMemo(() => {
    if (fixedFournisseur) return fixedFournisseur;
    return fournisseurs.find(f => sameId(f._id, selectedFournisseurId)) || null;
  }, [fixedFournisseur, fournisseurs, selectedFournisseurId]);

  const fournisseurOptions = useMemo(() => {
    if (!selectedTypeId) return fournisseurs;
    return fournisseurs.filter((f) => getFournisseurTypes(f).some(tp => sameId(tp?._id || tp, selectedTypeId)));
  }, [fournisseurs, selectedTypeId]);

  const availableProducts = useMemo(() => {
    if (isFixedSupplier) return produits;
    if (!selectedTypeId) return [];
    return produits.filter(p => sameId(getTypeId(p), selectedTypeId));
  }, [isFixedSupplier, produits, selectedTypeId]);

  const sourceNumero = initialDoc?.numero || 'BC selectionne';
  const nbSelectionnes = Object.keys(produitsQte).length;
  const requiresSourceBc = docType === 'bon_livraison';
  const sourceMissing = requiresSourceBc && !sourceId;

  const totalProduits = useMemo(() => {
    return Object.entries(produitsQte).reduce((sum, [id, line]) => {
      const produit = produits.find(p => sameId(p._id, id));
      if (!produit) return sum;
      const lineWithPricing = buildLinePricing(produit, line);
      if (remise2Mode !== 'produit') lineWithPricing.remise2 = 0;
      if (line?.mode === 'lineaire') {
        return sum + calculateLineAmounts(Number(recalculateLineaireEntry(line).metres) || 0, lineWithPricing).montantHT;
      }
      return sum + calculateLineAmounts(Number(line?.qte) || 1, lineWithPricing).montantHT;
    }, 0);
  }, [produits, produitsQte, remise2Mode]);

  const documentTotals = useMemo(() => {
    let brutSum = 0;
    let remise1Sum = 0;
    let remise2LineSum = 0;
    let montantHTSum = 0;
    let tvaSum = 0;

    Object.entries(produitsQte).forEach(([id, line]) => {
      const produit = produits.find(p => sameId(p._id, id));
      if (!produit) return;
      const quantite = line?.mode === 'lineaire'
        ? Number(recalculateLineaireEntry(line).metres) || 0
        : Number(line?.qte) || 1;
      const pricing = buildLinePricing(produit, line);
      if (remise2Mode !== 'produit') {
        pricing.remise2 = 0;
      }
      const amounts = calculateLineAmounts(quantite, pricing);
      brutSum += amounts.brut;
      remise1Sum += amounts.montantRemise1;
      remise2LineSum += amounts.montantRemise2;
      montantHTSum += amounts.montantHT;
      tvaSum += amounts.montantTVA;
    });

    let remise2GlobalAmt = 0;
    const r2Val = Math.max(0, Number(remise2Val) || 0);
    if (remise2Mode === 'global' && r2Val > 0) {
      if (remise2Type === 'montant') {
        remise2GlobalAmt = Math.min(montantHTSum, r2Val);
      } else {
        remise2GlobalAmt = montantHTSum * (Math.min(100, r2Val) / 100);
      }
    }

    const finalHT = Math.max(0, montantHTSum - remise2GlobalAmt);
    
    let fodecAmount = 0;
    if (fodecApplicable) {
      fodecAmount = finalHT * ((Number(tauxFodec) || 1) / 100);
    }
    const baseTVA = finalHT + fodecAmount;

    if (montantHTSum > 0 && remise2GlobalAmt > 0) {
      const ratio = finalHT / montantHTSum;
      tvaSum = tvaSum * ratio;
    }
    
    if (fodecApplicable && finalHT > 0) {
      const fodecRatio = fodecAmount / finalHT;
      tvaSum = tvaSum * (1 + fodecRatio);
    }

    const totalRemise2 = (remise2Mode === 'produit' ? remise2LineSum : 0) + remise2GlobalAmt;
    const totalRemise = remise1Sum + totalRemise2;

    return {
      brut: brutSum,
      remise1: remise1Sum,
      remise2: totalRemise2,
      remise: totalRemise,
      montantHT: finalHT,
      fodecAmount,
      baseTVA,
      tva: tvaSum,
      montantTTC: finalHT + fodecAmount + tvaSum
    };
  }, [produits, produitsQte, remise2Mode, remise2Val, remise2Type, fodecApplicable, tauxFodec]);

  const fraisTotal = docFrais.reduce((sum, frais) => sum + (Number(frais.prix) || 0), 0);
  let timbreAmount = 0;
  if (timbreFiscalApplicable === true) {
    timbreAmount = 1.000;
  } else if (timbreFiscalApplicable === undefined && ['devis', 'facture'].includes(docType)) {
    timbreAmount = 1.000;
  }
  const totalDocument = documentTotals.montantTTC + fraisTotal + timbreAmount;

  const toggleProduit = (produit) => {
    const id = produit?._id;
    if (!id) return;
    setProduitsQte(prev => {
      if (prev[id] !== undefined) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return { ...prev, [id]: buildDefaultSelection(produit) };
    });
  };

  const setQte = (id, value) => {
    const qte = Math.max(1, parseInt(value, 10) || 1);
    setProduitsQte(prev => {
      const current = prev[id] || { mode: 'unite', qte: 1 };
      return { ...prev, [id]: { ...current, mode: 'unite', qte } };
    });
  };

  const setLinePricingField = (id, field, value) => {
    setProduitsQte(prev => {
      const current = prev[id] || { mode: 'unite', qte: 1 };
      const numericValue = Number(value);
      const sanitizedValue = Number.isFinite(numericValue) ? numericValue : 0;
      return {
        ...prev,
        [id]: {
          ...current,
          [field]: field === 'remise'
            ? Math.min(100, Math.max(0, sanitizedValue))
            : Math.max(0, sanitizedValue)
        }
      };
    });
  };

  const setLineaireDecoupe = (id, decoupeIndex, field, value) => {
    setProduitsQte(prev => {
      const current = prev[id] || buildDefaultSelection({ modeGestionStock: 'lineaire' });
      const decoupes = Array.isArray(current.decoupes) && current.decoupes.length > 0
        ? [...current.decoupes]
        : [{ nombrePieces: 1, longueurPieceMetre: 1 }];
      const safeIndex = Math.max(0, decoupeIndex);
      while (decoupes.length <= safeIndex) decoupes.push({ nombrePieces: 1, longueurPieceMetre: 1 });
      const target = { ...decoupes[safeIndex] };

      if (field === 'nombrePieces') target.nombrePieces = Math.max(1, parseInt(value, 10) || 1);
      if (field === 'longueurPieceMetre') target.longueurPieceMetre = Math.max(0.001, Number(value) || 0.001);
      decoupes[safeIndex] = target;

      return { ...prev, [id]: { ...current, ...recalculateLineaireEntry({ decoupes }) } };
    });
  };

  const addLineaireDecoupe = (id) => {
    setProduitsQte(prev => {
      const current = prev[id] || buildDefaultSelection({ modeGestionStock: 'lineaire' });
      const decoupes = Array.isArray(current.decoupes) && current.decoupes.length > 0
        ? [...current.decoupes]
        : [{ nombrePieces: 1, longueurPieceMetre: 1 }];
      decoupes.push({ nombrePieces: 1, longueurPieceMetre: 1 });
      return { ...prev, [id]: { ...current, ...recalculateLineaireEntry({ decoupes }) } };
    });
  };

  const removeLineaireDecoupe = (id, decoupeIndex) => {
    setProduitsQte(prev => {
      const current = prev[id];
      const decoupes = Array.isArray(current?.decoupes) ? [...current.decoupes] : [];
      if (decoupes.length <= 1) return prev;
      decoupes.splice(decoupeIndex, 1);
      return { ...prev, [id]: { ...current, ...recalculateLineaireEntry({ decoupes }) } };
    });
  };

  const addFrais = () => setDocFrais(prev => [...prev, { label: '', prix: 0 }]);
  const updateFrais = (index, field, value) => {
    setDocFrais(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };
  const removeFrais = (index) => setDocFrais(prev => prev.filter((_, i) => i !== index));

  const buildProduitsPayload = () => Object.entries(produitsQte).map(([id, line]) => {
    const produit = produits.find(p => sameId(p._id, id));
    const pricing = buildLinePricing(produit, line);
    const r2 = remise2Mode === 'produit' ? Math.min(100, Math.max(0, Number(pricing.remise2) || 0)) : 0;

    if (line?.mode === 'lineaire') {
      const computed = recalculateLineaireEntry(line);
      return {
        id,
        prixUnitaire: Math.max(0, Number(pricing.prixUnitaire) || 0),
        tva: Math.max(0, Number(pricing.tva ?? 19)),
        remise: Math.min(100, Math.max(0, Number(pricing.remise) || 0)),
        remise2: r2,
        decoupes: computed.decoupes.map(d => ({
          nombrePieces: d.nombrePieces,
          longueurPieceMetre: d.longueurPieceMetre
        })),
        metres: computed.metres
      };
    }

    return {
      id,
      qte: Math.max(1, parseInt(line?.qte, 10) || 1),
      prixUnitaire: Math.max(0, Number(pricing.prixUnitaire) || 0),
      tva: Math.max(0, Number(pricing.tva ?? 19)),
      remise: Math.min(100, Math.max(0, Number(pricing.remise) || 0)),
      remise2: r2
    };
  });

  const sendDocumentEmail = async (document) => {
    await axios.post(`${API_URL}/documents-financiers/${document._id}/envoyer-email`, {
      destinataire: selectedFournisseur?.email || '',
      sujet: `${docTypeLabel(document.type)} - ${document.numero}`,
      message: `Veuillez trouver ci-joint le ${docTypeLabel(document.type)} ${document.numero}.`
    }, { headers: authHeader() });
  };

  const openPostCreationActions = async (document) => {
    if (!document || document.type === 'bon_commande') return;
    const result = await Swal.fire({
      title: `Document ${docTypeLabel(document.type)} cree`,
      html: `<p>Que souhaitez-vous faire avec le document <strong>${document.numero}</strong> ?</p>`,
      icon: 'success',
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: '<i class="fas fa-envelope"></i> Envoyer Mail',
      denyButtonText: '<i class="fab fa-whatsapp"></i> WhatsApp',
      cancelButtonText: 'Fermer',
      confirmButtonColor: '#2563eb',
      denyButtonColor: '#22c55e',
      cancelButtonColor: '#64748b'
    });

    if (result.isConfirmed) {
      try {
        await sendDocumentEmail(document);
        Swal.fire('Succes', 'Email envoye avec succes.', 'success');
      } catch (err) {
        Swal.fire('Erreur', err.response?.data?.message || 'Erreur lors de l envoi email.', 'error');
      }
    } else if (result.isDenied) {
      const phone = selectedFournisseur?.telephone || '';
      const cleanPhone = phone.replace(/\D/g, '');
      const text = encodeURIComponent(`Bonjour,\nVeuillez trouver ci-joint notre ${docTypeLabel(document.type)} numero ${document.numero}.`);
      window.open(`https://wa.me/${cleanPhone}?text=${text}`, '_blank', 'noopener,noreferrer');
    }
  };

  const submitDocument = async (event) => {
    event.preventDefault();
    if (!selectedFournisseurId) {
      Swal.fire('Attention', 'Veuillez selectionner un fournisseur.', 'warning');
      return;
    }
    if (nbSelectionnes === 0) {
      Swal.fire('Attention', 'Veuillez selectionner au moins un produit.', 'warning');
      return;
    }
    if (sourceMissing) {
      Swal.fire('Action impossible', 'Un Bon de Livraison fournisseur doit etre cree depuis un Bon de Commande valide.', 'warning');
      return;
    }
    if (docType === 'bon_commande' && sendBonCommandeEmail && !selectedFournisseur?.email) {
      Swal.fire('Email manquant', 'Ce fournisseur n a pas d email renseigne. Decochez l option ou ajoutez une adresse email.', 'warning');
      return;
    }

    const produitsAvecQuantites = buildProduitsPayload();
    const payload = {
      type: docType,
      description: docDescription,
      notes: docNotes,
      conditions: docConditions,
      remise2Mode,
      remise2Val: Number(remise2Val) || 0,
      remise2Type,
      fodecApplicable,
      tauxFodec: Number(tauxFodec) || 1,
      timbreFiscalApplicable,
      fraisSupplementaires: docFrais.map(f => ({ ...f, prix: Number(f.prix) || 0 })),
      produitsSelectionnes: produitsAvecQuantites.map(p => p.id),
      produitsAvecQuantites
    };

    if (docType === 'bon_livraison') payload.bonCommandeRef = sourceId;
    if (isEditMode) payload.fournisseurId = selectedFournisseurId;

    setSubmitting(true);
    try {
      const res = isEditMode
        ? await axios.put(
            `${API_URL}/documents-financiers/${initialDoc._id}`,
            payload,
            { headers: authHeader() }
          )
        : await axios.post(
            `${API_URL}/documents-financiers/fournisseur/${selectedFournisseurId}`,
            payload,
            { headers: authHeader() }
          );
      const savedDoc = res.data.document;

      if (isEditMode) {
        Swal.fire('Succes', `${docTypeLabel(docType)} ${savedDoc?.numero || ''} modifie avec succes.`, 'success');
      } else if (docType === 'bon_commande' && sendBonCommandeEmail && savedDoc?._id) {
        try {
          await sendDocumentEmail(savedDoc);
          Swal.fire('Succes', `Bon de Commande ${savedDoc.numero} cree et envoye par email.`, 'success');
        } catch (emailErr) {
          Swal.fire('Document cree', `Bon de Commande ${savedDoc.numero} cree, mais l email n a pas pu etre envoye.`, 'warning');
        }
      } else {
        Swal.fire('Succes', `${docTypeLabel(docType)} ${savedDoc?.numero || ''} cree avec succes.`, 'success');
      }

      await refreshHistorique?.();
      await onCreated?.(savedDoc);
      if (!isEditMode) await openPostCreationActions(savedDoc);
    } catch (err) {
      Swal.fire('Erreur', err.response?.data?.message || `Erreur lors de ${isEditMode ? 'la modification' : 'la creation'} du ${docTypeLabel(docType).toLowerCase()}.`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const changeTypeToBonCommande = () => {
    if (isEditMode) return;
    setDocType('bon_commande');
  };

  const changeTypeToBonLivraison = () => {
    if (isEditMode) return;
    if (!sourceId) {
      Swal.fire('Action indisponible', 'Le Bon de Livraison doit etre cree depuis un Bon de Commande valide.', 'info');
      return;
    }
    setDocType('bon_livraison');
  };

  return (
    <div className="fbf-page">
      <div className="fbf-toolbar">
        <button type="button" className="fbf-back-btn" onClick={onBack}>
          <i className="fas fa-arrow-left"></i>
          Retour
        </button>
        <div>
          <span className="fbf-kicker">{contextLabel}</span>
          <h1>{isEditMode ? 'Modification' : 'Creation'} {docType === 'bon_livraison' ? 'Bon de Livraison' : 'Bon de Commande'}</h1>
          <p>Formulaire fournisseur partage entre le detail fournisseur et la gestion produits.</p>
        </div>
      </div>

      <form className="fbf-shell" onSubmit={submitDocument}>
        <section className="fbf-main">
          <div className="fbf-section fbf-workflow">
            <div className="fbf-section-title">
              <span>Workflow</span>
              <strong>BC &gt; BL</strong>
            </div>
            <div className="fbf-type-switcher">
              <button type="button" className={docType === 'bon_commande' ? 'active' : ''} onClick={changeTypeToBonCommande} disabled={isEditMode || initialType === 'bon_livraison'}>
                <i className="fas fa-file-invoice"></i>
                Bon de Commande
              </button>
              <button type="button" className={docType === 'bon_livraison' ? 'active' : ''} onClick={changeTypeToBonLivraison} disabled={isEditMode}>
                <i className={`fas ${sourceId ? 'fa-truck' : 'fa-lock'}`}></i>
                Bon de Livraison
              </button>
            </div>
            <div className={`fbf-alert ${sourceId ? 'info' : 'warning'}`}>
              <i className={`fas ${sourceId ? 'fa-link' : 'fa-exclamation-triangle'}`}></i>
              <span>
                {sourceId
                  ? `BL rattache au Bon de Commande source: ${sourceNumero}.`
                  : 'Un BL fournisseur ne peut pas etre cree directement. Convertissez un BC valide en BL.'}
              </span>
            </div>
          </div>

          {!isFixedSupplier && (
            <div className="fbf-section">
              <div className="fbf-section-title">
                <span>Selection</span>
                <strong>Categorie et fournisseur</strong>
              </div>
              <div className="fbf-grid-2">
                <label className="fbf-field">
                  <span>Categorie produits *</span>
                  <select
                    value={selectedTypeId}
                    onChange={(event) => {
                      setSelectedTypeId(event.target.value);
                      setSelectedFournisseurId('');
                      setProduitsQte({});
                    }}
                    required
                  >
                    <option value="">Selectionner une categorie</option>
                    {types.map(type => <option key={type._id} value={type._id}>{type.label}</option>)}
                  </select>
                </label>
                <label className="fbf-field">
                  <span>Fournisseur *</span>
                  <select
                    value={selectedFournisseurId}
                    onChange={(event) => {
                      setSelectedFournisseurId(event.target.value);
                      setProduitsQte({});
                    }}
                    required
                    disabled={!selectedTypeId || Boolean(sourceId)}
                  >
                    <option value="">Selectionner un fournisseur</option>
                    {fournisseurOptions.map(f => <option key={f._id} value={f._id}>{f.raisonSociale}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}

          {/* ── Section Configuration Remise 2 ── */}
          <div className="fbf-section">
            <div className="fbf-section-title">
              <span>Remise additionnelle</span>
              <strong>2ᵉ Remise (Optionnelle)</strong>
            </div>
            <div className="fbf-grid-2">
              <label className="fbf-field">
                <span>Application 2ᵉ remise</span>
                <select
                  value={remise2Mode}
                  onChange={e => setRemise2Mode(e.target.value)}
                >
                  <option value="aucun">Aucune (Pas de 2ᵉ remise)</option>
                  <option value="produit">Par produit (Saisie sur chaque produit)</option>
                  <option value="global">Sur le total du document</option>
                </select>
              </label>
              {remise2Mode === 'global' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <label className="fbf-field">
                    <span>Valeur remise 2</span>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      value={remise2Val}
                      onChange={e => setRemise2Val(Math.max(0, Number(e.target.value) || 0))}
                      placeholder="0"
                    />
                  </label>
                  <label className="fbf-field">
                    <span>Type</span>
                    <select
                      value={remise2Type}
                      onChange={e => setRemise2Type(e.target.value)}
                    >
                      <option value="pourcentage">Pourcentage (%)</option>
                      <option value="montant">Montant fixe (DT)</option>
                    </select>
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="fbf-section">
            <div className="fbf-section-title">
              <span>Produits</span>
              <strong>{nbSelectionnes} selectionne(s)</strong>
            </div>
            {availableProducts.length === 0 ? (
              <div className="fbf-empty">
                <i className="fas fa-box-open"></i>
                <span>{isFixedSupplier ? 'Aucun produit lie a ce fournisseur.' : 'Selectionnez une categorie pour afficher les produits.'}</span>
              </div>
            ) : (
              <div className="fbf-products-list">
                {availableProducts.map((produit) => {
                  const lineConfig = produitsQte[produit._id] || null;
                  const selected = produitsQte[produit._id] !== undefined;
                  const lineaire = isProduitLineaire(produit);
                  const qte = lineConfig?.qte || 1; 
                  const decoupes = lineaire ? recalculateLineaireEntry(lineConfig || buildDefaultSelection(produit)).decoupes : [];
                  const metres = lineaire ? Number(decoupes.reduce((sum, d) => sum + d.metres, 0).toFixed(3)) : 0;
                  const pricing = buildLinePricing(produit, lineConfig);
                  if (remise2Mode !== 'produit') pricing.remise2 = 0;
                  const prixUnitaire = Number(pricing.prixUnitaire) || 0;
                  const quantiteLigne = lineaire ? metres : qte;
                  const amounts = calculateLineAmounts(quantiteLigne, pricing);

                  return (
                    <article key={produit._id} className={`fbf-product-row ${selected ? 'selected' : ''}`}>
                      <label className="fbf-product-head">
                        <input type="checkbox" checked={selected} onChange={() => toggleProduit(produit)} />
                        <span>
                          <strong>{produit.nomProduit}</strong>
                          <small style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginTop: '3px', lineHeight: '1.4' }}>
                            {produit.reference && (
                              <span style={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                                Ref: {produit.reference} ·
                              </span>
                            )}
                            <span className="fbf-stock-badge" style={{ whiteSpace: 'nowrap', fontWeight: '600', color: '#334155', background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px', display: 'inline-block' }}>
                              Stock: {formatStockQuantity(produit.quantite, lineaire)}
                            </span>
                            <span style={{ whiteSpace: 'nowrap', color: '#059669', fontWeight: '600' }}>
                              · {formatCurrency(prixUnitaire)} / {lineaire ? 'm' : 'unite'}
                            </span>
                            <span style={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                              · TVA {pricing.tva}%
                            </span>
                            <span style={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                              · Remise 1 {pricing.remise}%
                            </span>
                            {remise2Mode === 'produit' && (
                              <span style={{ whiteSpace: 'nowrap', color: '#64748b' }}>
                                · Remise 2 {pricing.remise2}%
                              </span>
                            )}
                          </small>
                        </span>
                      </label>
                      {selected && (
                        <div className="fbf-product-controls">
                          {lineaire ? (
                            <div className="fbf-decoupes">
                              {decoupes.map((decoupe, index) => (
                                <div className="fbf-decoupe-row" key={`${produit._id}-${index}`}>
                                  <label>
                                    Pieces
                                    <input type="number" min="1" value={decoupe.nombrePieces} onChange={event => setLineaireDecoupe(produit._id, index, 'nombrePieces', event.target.value)} />
                                  </label>
                                  <label>
                                    Longueur/p
                                    <input type="number" min="0.001" step="0.001" value={decoupe.longueurPieceMetre} onChange={event => setLineaireDecoupe(produit._id, index, 'longueurPieceMetre', event.target.value)} />
                                  </label>
                                  <strong>{decoupe.metres.toLocaleString('fr-FR')} m</strong>
                                  {decoupes.length > 1 && (
                                    <button type="button" className="fbf-icon-danger" onClick={() => removeLineaireDecoupe(produit._id, index)} title="Retirer cette decoupe">
                                      <i className="fas fa-times"></i>
                                    </button>
                                  )}
                                </div>
                              ))}
                              <button type="button" className="fbf-mini-btn" onClick={() => addLineaireDecoupe(produit._id)}>
                                <i className="fas fa-plus"></i>
                                Decoupe
                              </button>
                              <div className="fbf-line-total">Total: {metres.toLocaleString('fr-FR')} m - {formatCurrency(amounts.montantTTC)} TTC</div>
                            </div>
                          ) : (
                            <div className="fbf-qte-control">
                              <button type="button" onClick={() => setQte(produit._id, qte - 1)}>-</button>
                              <input type="number" min="1" value={qte} onChange={event => setQte(produit._id, event.target.value)} />
                              <button type="button" onClick={() => setQte(produit._id, qte + 1)}>+</button>
                              <strong>{formatCurrency(amounts.montantTTC)} TTC</strong>
                            </div>
                          )}
                          <div className="fbf-pricing-controls">
                            <label>
                              Prix unitaire
                              <input type="number" min="0" step="0.001" value={pricing.prixUnitaire} onChange={event => setLinePricingField(produit._id, 'prixUnitaire', event.target.value)} />
                            </label>
                            <label>
                              TVA (%)
                              <input type="number" min="0" step="0.001" value={pricing.tva} onChange={event => setLinePricingField(produit._id, 'tva', event.target.value)} />
                            </label>
                            <label>
                              Remise 1 (%)
                              <input type="number" min="0" max="100" step="0.001" value={pricing.remise} onChange={event => setLinePricingField(produit._id, 'remise', event.target.value)} />
                            </label>
                            {remise2Mode === 'produit' && (
                              <label>
                                Remise 2 (%)
                                <input type="number" min="0" max="100" step="0.001" value={pricing.remise2} onChange={event => setLinePricingField(produit._id, 'remise2', event.target.value)} />
                              </label>
                            )}
                            <strong>{formatCurrency(amounts.montantHT)} HT</strong>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          <div className="fbf-section">
            <div className="fbf-section-title">
              <span>Taxes additionnelles</span>
              <strong>FODEC et Timbre fiscal</strong>
            </div>
            <div className="fbf-grid-2" style={{ gap: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <input 
                  type="checkbox" 
                  checked={fodecApplicable} 
                  onChange={(e) => setFodecApplicable(e.target.checked)} 
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '600', color: '#1e293b' }}>Appliquer FODEC (1 %)</span>
              </label>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <input 
                  type="checkbox" 
                  checked={timbreFiscalApplicable} 
                  onChange={(e) => setTimbreFiscalApplicable(e.target.checked)} 
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ fontWeight: '600', color: '#1e293b' }}>Appliquer Timbre fiscal (1,000 DT)</span>
              </label>
            </div>
          </div>

          <div className="fbf-section">
            <div className="fbf-section-title">
              <span>Details</span>
              <strong>Conditions et notes</strong>
            </div>
            <label className="fbf-field">
              <span>Description</span>
              <textarea rows="3" value={docDescription} onChange={event => setDocDescription(event.target.value)} placeholder="Details du document" />
            </label>
            {docType === 'bon_livraison' && (
              <div className="fbf-frais">
                <div className="fbf-inline-title">
                  <strong>Frais supplementaires</strong>
                  <button type="button" className="fbf-mini-btn" onClick={addFrais}>
                    <i className="fas fa-plus"></i>
                    Ajouter
                  </button>
                </div>
                {docFrais.map((frais, index) => (
                  <div className="fbf-frais-row" key={`frais-${index}`}>
                    <input type="text" value={frais.label} onChange={event => updateFrais(index, 'label', event.target.value)} placeholder="Libelle" />
                    <input type="number" min="0" step="0.001" value={frais.prix} onChange={event => updateFrais(index, 'prix', event.target.value)} placeholder="Prix TND" />
                    <button type="button" className="fbf-icon-danger" onClick={() => removeFrais(index)} title="Supprimer ce frais">
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="fbf-grid-2">
              <label className="fbf-field">
                <span>Conditions</span>
                <input type="text" value={docConditions} onChange={event => setDocConditions(event.target.value)} placeholder="Ex: Paiement a 30 jours" />
              </label>
              <label className="fbf-field">
                <span>Notes internes</span>
                <textarea rows="2" value={docNotes} onChange={event => setDocNotes(event.target.value)} placeholder="Notes internes" />
              </label>
            </div>
          </div>
        </section>

        <aside className="fbf-summary">
          <div className="fbf-summary-card">
            <span className="fbf-kicker">Resume</span>
            <h2>{docTypeLabel(docType)}</h2>
            <dl>
              <div>
                <dt>Fournisseur</dt>
                <dd>{selectedFournisseur?.raisonSociale || '-'}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>{selectedFournisseur?.email || 'Non renseigne'}</dd>
              </div>
              <div>
                <dt>Produits</dt>
                <dd>{nbSelectionnes}</dd>
              </div>
              <div>
                <dt>Total HT produits</dt>
                <dd>{formatCurrency(totalProduits)}</dd>
              </div>
              <div>
                <dt>Total Remises</dt>
                <dd>{formatCurrency(documentTotals.remise)}</dd>
              </div>
              {documentTotals.remise2 > 0 && (
                <div>
                  <dt style={{ fontSize: '0.8rem', color: '#64748b' }}>dont 2ᵉ remise</dt>
                  <dd style={{ fontSize: '0.8rem', color: '#64748b' }}>{formatCurrency(documentTotals.remise2)}</dd>
                </div>
              )}
              <div>
                <dt>Total HT Net</dt>
                <dd>{formatCurrency(documentTotals.montantHT)}</dd>
              </div>
              
              {fodecApplicable && documentTotals.fodecAmount > 0 && (
                <>
                  <div>
                    <dt>FODEC (1 %)</dt>
                    <dd>{formatCurrency(documentTotals.fodecAmount)}</dd>
                  </div>
                  <div>
                    <dt>Base TVA</dt>
                    <dd>{formatCurrency(documentTotals.baseTVA)}</dd>
                  </div>
                </>
              )}

              <div>
                <dt>TVA</dt>
                <dd>{formatCurrency(documentTotals.tva)}</dd>
              </div>
              
              {timbreAmount > 0 && (
                <div>
                  <dt>Timbre fiscal</dt>
                  <dd>{formatCurrency(timbreAmount)}</dd>
                </div>
              )}
              {docType === 'bon_livraison' && (
                <div>
                  <dt>Frais</dt>
                  <dd>{formatCurrency(fraisTotal)}</dd>
                </div>
              )}
              <div className="fbf-total-row">
                <dt>Total estime</dt>
                <dd>{formatCurrency(totalDocument)}</dd>
              </div>
            </dl>
            {docType === 'bon_commande' && (
              <label className="fbf-email-option">
                <input type="checkbox" checked={sendBonCommandeEmail} onChange={event => setSendBonCommandeEmail(event.target.checked)} />
                <span>
                  <strong>Envoyer le BC par email</strong>
                  <small>Desactive par defaut.</small>
                </span>
              </label>
            )}
            <div className="fbf-actions">
              <button type="button" className="fbf-secondary-btn" onClick={onBack}>Annuler</button>
              <button type="submit" className="fbf-primary-btn" disabled={submitting || !selectedFournisseurId || nbSelectionnes === 0 || sourceMissing}>
                <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                {submitting ? (isEditMode ? 'Modification...' : 'Creation...') : `${isEditMode ? 'Modifier' : 'Creer'} ${docType === 'bon_livraison' ? 'le BL' : 'le BC'}`}
              </button>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
};

export default FournisseurDocumentFormPage;
